import axios from "axios";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import toast from "react-hot-toast";
import { Preloader } from "../components/elements/Preloader";
import {
  getAllIncompleteUsers,
  getFlexibleUsers,
  getOutrightUsers,
} from "../redux/thunk";

const PushNotication = () => {
  const { token } = useAppSelector((store) => store.auth);
  const [multipleEmails, setMultipleEmails] = useState("");
  const [multipleTitle, setMultipleTitle] = useState("");
  const [multipleBody, setMultipleBody] = useState("");
  const [allCustomersTitle, setAllCustomersTitle] = useState("");
  const [allCustomersBody, setAllCustomersBody] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [messageTitle, setMessageTitle] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMultiple, setIsLoadingMultiple] = useState(false);
  const [isLoadingAllCustomers, setIsLoadingAllCustomers] = useState(false);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [outright, setOutright] = useState<any[]>([]);

  const validateSingleForm = () => {
    let isValid = true;
    const newErrors: any = {};

    if (!customerEmail) {
      newErrors.email = "Please enter customer email";
      isValid = false;
    }
    if (!messageTitle) {
      newErrors.messageTitle = "Please enter message title";
      isValid = false;
    }
    if (!messageBody) {
      newErrors.messageBody = "Please enter the message";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const parseMultipleEmails = () =>
    multipleEmails
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);

  const validateMultipleCustomersForm = () => {
    const emails = parseMultipleEmails();
    const newErrors: any = {};
    let isValid = true;
    if (emails.length === 0) {
      newErrors.multiEmails = "Please enter at least one customer email";
      isValid = false;
    }
    if (!multipleTitle.trim()) {
      newErrors.multiTitle = "Please enter message title";
      isValid = false;
    }
    if (!multipleBody.trim()) {
      newErrors.multiBody = "Please enter the message";
      isValid = false;
    }
    setErrors((prev: Record<string, string>) => {
      const { multiEmails, multiTitle, multiBody, ...rest } = prev;
      return { ...rest, ...newErrors };
    });
    return isValid;
  };

  const validateAllCustomersForm = () => {
    const newErrors: any = {};
    let isValid = true;
    if (!allCustomersTitle.trim()) {
      newErrors.allCustomersTitle = "Please enter message title";
      isValid = false;
    }
    if (!allCustomersBody.trim()) {
      newErrors.allCustomersBody = "Please enter the message";
      isValid = false;
    }
    setErrors((prev: Record<string, string>) => {
      const { allCustomersTitle: _t, allCustomersBody: _b, ...rest } = prev;
      return { ...rest, ...newErrors };
    });
    return isValid;
  };

  const dispatch = useAppDispatch();

  useEffect(() => {
    const getAllOutrightUsers = async () => {
      setIsUsersLoading(true);
      let currentPage = 1;
      let totalPages = 1;
      let allUsers: any[] = [];
      do {
        const res = await dispatch(
          getOutrightUsers({
            page: currentPage,
            limit: 50,
          }),
        );
        if (res.payload.data && res.payload.data.data) {
          allUsers = [...allUsers, ...res.payload.data.data];
          totalPages = res.payload.data.pagination?.totalPages || 1;
        }

        currentPage++;
      } while (currentPage <= totalPages);
      setOutright(allUsers);
      setIsUsersLoading(false);
    };
    getAllOutrightUsers();
    dispatch(getFlexibleUsers());
    dispatch(getAllIncompleteUsers());
  }, [dispatch, token]);

  const sendSingleNotification = async () => {
    if (!validateSingleForm()) {
      toast.error("Please complete the form to send message");
      return;
    }

    setIsLoading(true);

    const payload = {
      emails: [customerEmail.trim().toLowerCase()],
      title: messageTitle,
      body: messageBody,
    };

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/sendPushNotificationToMultipleEmails`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      toast.success(res.data.message || "Sent successfully");
      setCustomerEmail("");
      setMessageTitle("");
      setMessageBody("");
    } catch (error: any) {
      console.error("Failed to send notification:", error);
      toast.error(
        error?.response?.data?.message || "Failed to send notification",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const sendMultipleCustomersNotification = async () => {
    if (!validateMultipleCustomersForm()) {
      toast.error("Please complete the form to send message");
      return;
    }

    const emails = parseMultipleEmails().map((e) => e.toLowerCase());
    const payload = {
      emails,
      title: multipleTitle.trim(),
      body: multipleBody.trim(),
    };

    setIsLoadingMultiple(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/sendPushNotificationToMultipleEmails`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      toast.success(res.data.message || "Sent successfully");
      setMultipleEmails("");
      setMultipleTitle("");
      setMultipleBody("");
    } catch (error) {
      console.error("Failed to send notification:", error);
      toast.error("Failed to send notification");
    } finally {
      setIsLoadingMultiple(false);
    }
  };

  const sendAllCustomersNotification = async () => {
    if (!validateAllCustomersForm()) {
      toast.error("Please complete the form to send message");
      return;
    }

    const payload = {
      title: allCustomersTitle.trim(),
      body: allCustomersBody.trim(),
      emails: [],
    };

    setIsLoadingAllCustomers(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/sendMultipleNotification`,
        payload,
        {
          headers: {
            Authorization: token,
          },
        },
      );

      toast.success(res.data.message || "Sent successfully");
      setAllCustomersTitle("");
      setAllCustomersBody("");
    } catch (error) {
      console.error("Failed to send notification:", error);
      toast.error("Failed to send notification");
    } finally {
      setIsLoadingAllCustomers(false);
    }
  };

  return (
    <main className="w-full">
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
          Push Notification
        </h1>
        <p className="text-gray-500 text-xs sm:text-sm">
          Send targeted and broadcast messages to your users
        </p>
      </div>
      <section className="p-5 m-3 rounded-md px-10 border">
        <h3 className="font-semibold text-lg mb-4">To One Customer</h3>
        <div>
          <label htmlFor="email" className="font-semibold">
            Customer Email Address:
          </label>
          <input
            type="email"
            name="email"
            id="email"
            placeholder="Abaya@lagos.com"
            className="border w-4/5 block my-3 text-sm p-2 rounded-md"
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
          />
        </div>
        {errors.email && (
          <p className="text-red-500 text-xs mb-2">{errors.email}</p>
        )}
        <div>
          <label htmlFor="title" className="font-semibold">
            Enter title of message:
          </label>
          <input
            type="text"
            name="title"
            id="title"
            placeholder="Hi ..."
            className="border w-4/5 block my-3 text-sm p-2 rounded-md"
            value={messageTitle}
            onChange={(e) => setMessageTitle(e.target.value)}
          />
        </div>
        {errors.messageTitle && (
          <p className="text-red-500 text-xs mb-2">{errors.messageTitle}</p>
        )}
        <div>
          <label htmlFor="message" className="font-semibold">
            Add message body:
          </label>
          <textarea
            name="message"
            id="message"
            rows={10}
            value={messageBody}
            onChange={(e) => setMessageBody(e.target.value)}
            className="border w-4/5 block my-3 text-sm p-2 rounded-md"
            placeholder="Dear friends ..."
          ></textarea>
        </div>
        {errors.messageBody && (
          <p className="text-red-500 text-xs mb-2">{errors.messageBody}</p>
        )}
        <button
          type="button"
          onClick={sendSingleNotification}
          disabled={isLoading}
          className="bg-secondary text-sm p-3 text-white rounded-md"
        >
          {isLoading ? <Preloader /> : "Send"}
        </button>
      </section>

      <section className="p-5 m-3 rounded-md px-10 border">
        <h3 className="font-semibold text-lg mb-4">To Multiple Customers</h3>
        <div>
          <label htmlFor="multiple-emails" className="font-semibold">
            Add customer emails (comma separated):
          </label>
          <textarea
            name="multiple-emails"
            id="multiple-emails"
            rows={6}
            value={multipleEmails}
            onChange={(e) => setMultipleEmails(e.target.value)}
            className="border w-4/5 block my-3 text-sm p-2 rounded-md"
          />
        </div>
        {errors.multiEmails && (
          <p className="text-red-500 text-xs mb-2">{errors.multiEmails}</p>
        )}
        <div>
          <label htmlFor="multiple-title" className="font-semibold">
            Enter title of message:
          </label>
          <input
            type="text"
            name="multiple-title"
            id="multiple-title"
            placeholder="Hi ..."
            className="border w-4/5 block my-3 text-sm p-2 rounded-md"
            value={multipleTitle}
            onChange={(e) => setMultipleTitle(e.target.value)}
          />
        </div>
        {errors.multiTitle && (
          <p className="text-red-500 text-xs mb-2">{errors.multiTitle}</p>
        )}
        <div>
          <label htmlFor="multiple-body" className="font-semibold">
            Add broadcast message:
          </label>
          <textarea
            name="multiple-body"
            id="multiple-body"
            rows={10}
            value={multipleBody}
            onChange={(e) => setMultipleBody(e.target.value)}
            className="border w-4/5 block my-3 text-sm p-2 rounded-md"
            placeholder="Dear friends ..."
          />
        </div>
        {errors.multiBody && (
          <p className="text-red-500 text-xs mb-2">{errors.multiBody}</p>
        )}
        <button
          type="button"
          onClick={sendMultipleCustomersNotification}
          disabled={isLoadingMultiple}
          className="bg-secondary text-sm p-3 text-white rounded-md"
        >
          {isLoadingMultiple ? <Preloader /> : "Send to multiple"}
        </button>
      </section>
      <section className="p-5 m-3 rounded-md px-10 border">
        <h3 className="font-semibold text-lg mb-4">To All Customers</h3>
        <div>
          <label htmlFor="all-customers-title" className="font-semibold">
            Enter title of message:
          </label>
          <input
            type="text"
            name="all-customers-title"
            id="all-customers-title"
            placeholder="Hi ..."
            className="border w-4/5 block my-3 text-sm p-2 rounded-md"
            value={allCustomersTitle}
            onChange={(e) => setAllCustomersTitle(e.target.value)}
          />
        </div>
        {errors.allCustomersTitle && (
          <p className="text-red-500 text-xs mb-2">{errors.allCustomersTitle}</p>
        )}
        <div>
          <label htmlFor="all-customers-body" className="font-semibold">
            Add broadcast message:
          </label>
          <textarea
            name="all-customers-body"
            id="all-customers-body"
            rows={10}
            value={allCustomersBody}
            onChange={(e) => setAllCustomersBody(e.target.value)}
            className="border w-4/5 block my-3 text-sm p-2 rounded-md"
            placeholder="Dear friends ..."
          />
        </div>
        {errors.allCustomersBody && (
          <p className="text-red-500 text-xs mb-2">{errors.allCustomersBody}</p>
        )}
        <button
          type="button"
          onClick={sendAllCustomersNotification}
          disabled={isLoadingAllCustomers}
          className="bg-secondary text-sm p-3 text-white rounded-md"
        >
          {isLoadingAllCustomers ? <Preloader /> : "Send to all customers"}
        </button>
      </section>
    </main>
  );
};

export default PushNotication;
